// The valley's small props that aren't building-kit archetypes (phase C joined): a camp's fire ring and the Warden's
// dais (the signpost and the notice board are the things stream's signs). Faceted in the low-poly look's palette, they merge into their
// chunk's one surface mesh; their colliders are in the piece's frame (base at the origin, facing +z), as the kit's.
// The building kit (@rpg/meshkit's `createKit`, the kit-pieces table) builds everything else the layout places.
import * as THREE from "three";
import type { ColliderSpec } from "@rpg/collision";
import { facet, merge, seeded } from "@rpg/meshkit";

/** The pieces built here (every other placed piece is the kit's). */
export const PROP_PIECES = ["camp", "ruins-dais"] as const;

/** A built prop: its faceted geometry (null when built without colours: the sim asking for colliders) and colliders. */
export interface Prop {
  geometry: THREE.BufferGeometry | null;
  colliders: ColliderSpec[];
}

/** Whether a piece is one of these props. */
export const isProp = (piece: string): boolean => (PROP_PIECES as readonly string[]).includes(piece);

/** Build a prop. `colour` (a palette lookup) makes its geometry; without it only the colliders. */
export function buildProp(piece: string, seed: number, colour?: (key: string) => THREE.Color): Prop {
  const rng = seeded(seed * 2654435761 + piece.length);
  const parts: THREE.BufferGeometry[] = [];
  const colliders: ColliderSpec[] = [];
  const b = (cx: number, cy: number, cz: number, w: number, h: number, d: number, c: string, jitter = 0.04, glow = 0): void => {
    if (colour) parts.push(facet(new THREE.BoxGeometry(w, h, d).translate(cx, cy, cz), { colour: colour(c), jitter, tint: 0.06, rng, glow }));
  };
  const cyl = (r0: number, r1: number, h: number, y: number, sides: number, c: string): void => {
    if (colour) parts.push(facet(new THREE.CylinderGeometry(r0, r1, h, sides).translate(0, y, 0), { colour: colour(c), jitter: 0.04, tint: 0.06, rng }));
  };
  switch (piece) {
    case "camp": {
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2;
        b(Math.cos(a) * 0.7, 0.12, Math.sin(a) * 0.7, 0.3, 0.24, 0.3, "stone.b", 0.04);
      }
      b(0, 0.12, 0, 0.9, 0.16, 0.16, "trunk", 0.01);
      // the embers glow by day and night
      b(0, 0.3, 0, 0.25, 0.4, 0.25, "#e8873a", 0, 1);
      break;
    }
    case "ruins-dais":
      cyl(2.6, 2.8, 0.3, 0.1, 9, "stone.b");
      break;
    default:
      throw new Error(`world props: no prop "${piece}" (have ${PROP_PIECES.join(", ")})`);
  }
  return { geometry: parts.length ? merge(parts) : null, colliders };
}
